import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { ArrowLeft, KeyRound } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import Logo3D from "@/components/Logo3D";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { API, useRx } from "@/context/AuthContext";

const BG = "https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?crop=entropy&cs=srgb&fm=jpg&q=70&w=1400";

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="hidden lg:flex relative w-1/2 overflow-hidden rx-noise">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="relative mt-auto p-12">
          <Logo3D size={56} textClass="text-4xl" testId="auth-logo" />
          <p className="text-gray-300 mt-3 max-w-md">Final signals, Strength Scores and exact levels. The engine stays on our servers.</p>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        <Link to="/" className="self-start lg:self-center text-xs text-gray-500 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
        {children}
      </div>
    </div>
  );
}

const DemoLink = () => (
  <Link to="/demo" data-testid="demo-login-link" className="text-xs text-gray-400 hover:text-[#00FF66] inline-flex items-center gap-1"><KeyRound size={12} /> Reviewer / demo access</Link>
);

export function SignInPage() {
  const { authed } = useRx();
  if (authed) return <Navigate to="/app" replace />;
  return <AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /><DemoLink /></AuthLayout>;
}

export function SignUpPage() {
  const { authed } = useRx();
  if (authed) return <Navigate to="/app" replace />;
  return <AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /><DemoLink /></AuthLayout>;
}

export function DemoLoginPage() {
  const { loginDemo } = useRx();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { await axios.post(`${API}/demo/request-otp`, { email }); setStep(2); }
    catch (x) { setErr(x.response?.data?.detail || "Could not send OTP"); }
    setBusy(false);
  };
  const verify = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { const { data } = await axios.post(`${API}/demo/verify`, { email, otp }); loginDemo(data.token, data.me); nav("/app"); }
    catch (x) { setErr(x.response?.data?.detail || "Invalid OTP"); }
    setBusy(false);
  };
  return (
    <AuthLayout>
      <div className="w-full max-w-sm rx-card p-7" data-testid="demo-login-card">
        <div className="rx-label text-[#007AFF]">Reviewer mode</div>
        <h1 className="font-display text-2xl font-bold mt-2">Demo access</h1>
        <p className="text-sm text-gray-400 mt-2">For app reviewers. Enter the reviewer email and the fixed one-time code you were given.</p>
        {step === 1 ? (
          <form onSubmit={send} className="mt-6 space-y-4">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reviewer email" data-testid="demo-email-input" className="bg-black/40 border-white/10" />
            <button disabled={busy} data-testid="demo-send-otp-btn" className="w-full rounded-full bg-[#007AFF] font-bold py-2.5 hover:bg-[#0056b3] transition-colors disabled:opacity-50">Continue</button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <div className="text-xs text-gray-400">Code for <span className="text-white">{email}</span></div>
            <InputOTP maxLength={6} value={otp} onChange={setOtp} autoFocus data-testid="demo-otp-input">
              <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="font-data border-white/15" />)}</InputOTPGroup>
            </InputOTP>
            <button disabled={busy || otp.length < 6} data-testid="demo-verify-btn" className="w-full rounded-full bg-[#00FF66] text-black font-bold py-2.5 hover:brightness-110 transition-[filter] disabled:opacity-50">Verify & enter</button>
            <button type="button" onClick={() => { setStep(1); setOtp(""); }} className="w-full text-xs text-gray-500 hover:text-white">Change email</button>
          </form>
        )}
        {err && <div className="mt-4 text-sm text-[#FF3366]" data-testid="demo-error">{err}</div>}
      </div>
    </AuthLayout>
  );
}
